import React, { Fragment, useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import classNames from 'classnames';

import {
  getEligibleTagsByIdentityGender,
  getInvalidJerseyNumbersFromSelectedTags,
  mapIdentityAlbumFolderTagsToSelectedTags,
  mapSelectedTagsToIdentityAlbumFolderTagsInput,
} from '../../../../../temp/views/onboarding/TagSelection/helpers';
import Alert from '../../../../../../components/global/Alert';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { IDENTITY_GENDER } from '../../../../../../constants/identity';
import Label from '../../../../../../components/global/Label';
import Select from '../../../../../../components/global/Select';
import Button from '../../../../../../components/global/Button';
import { getTeamLabel } from '../../../../../../util/album-folder-tags';

const propTypes = {
  albumFolderId: PropTypes.string.isRequired,
  identityId: PropTypes.string.isRequired,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      uniform_enabled: PropTypes.bool,
      uniform_numbered: PropTypes.bool,
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teams: PropTypes.shape({
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                  coach_name: PropTypes.string,
                }),
              ),
            }),
          }),
        ),
      }),
      gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)).isRequired,
    }),
  ),
  identityAlbumFolderTags: PropTypes.array,
  sportsTagsCreate: PropTypes.func.isRequired,
  selectedTag: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
  onCancel: PropTypes.func,
  includeTeamLevelData: PropTypes.bool,
};

const RosterIdentityTagAssignment = ({
  albumFolderId,
  identityId,
  tags = [],
  identityAlbumFolderTags,
  sportsTagsCreate,
  selectedTag,
  onComplete,
  onError,
  onCancel,
  className,
  includeTeamLevelData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMounted = useRef(true);

  const classes = classNames({
    'roster-identity-tag-selection-form': true,
    [className]: !!className,
  });

  const jerseyNumbers = useMemo(() => {
    const numbers = [];
    for (let i = 0; i < 100; i += 1) {
      if (i < 10) {
        numbers.push(`0${i}`);
      } else {
        numbers.push(`${i}`);
      }
    }
    return numbers;
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onFormSubmit = async values => {
    let input = mapSelectedTagsToIdentityAlbumFolderTagsInput(
      { id: identityId },
      values.selectedTags,
      values.configuringTagIds,
      !!includeTeamLevelData,
    );

    if (includeTeamLevelData) {
      input = {
        ...input,
        album_folder_tags: input.album_folder_tags.map(tag => {
          const activeTag = tags.find(t => t.id === tag.album_folder_tag_id);
          const level = activeTag?.levels?.records.find(
            ({ id }) => id === tag.uniform?.uniform_album_folder_tag_level_id,
          );
          const team = level?.teams?.records.find(
            ({ id }) =>
              id === tag.uniform?.uniform_album_folder_tag_level_team_id,
          );

          return {
            ...tag,
            team,
            level,
          };
        }),
      };
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      await sportsTagsCreate(input, {
        albumFolderId,
        identityId,
      });

      if (isMounted.current) {
        setSubmitting(false);
        onComplete?.();
      }
    } catch (error) {
      if (isMounted.current) {
        setSubmitError(getErrorString(error));
        setSubmitting(false);
        onError?.(error);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        configuringTagIds: selectedTag?.album_folder_tag?.id
          ? [selectedTag.album_folder_tag.id]
          : [],
        selectedTags: mapIdentityAlbumFolderTagsToSelectedTags(
          tags,
          identityAlbumFolderTags || [],
        ),
        gender: tags.gender || '',
        sportId: selectedTag?.album_folder_tag.id || '',
      }}
      validate={values => {
        const errors = {};

        const invalidJerseyNumbers = getInvalidJerseyNumbersFromSelectedTags(
          values.selectedTags,
        );

        if (invalidJerseyNumbers.length > 0) {
          errors.jerseyNumbers =
            'One or more jersey numbers are invalid. Please use a number between 00 and 99';
        }

        if (!values.sportId) {
          errors.sportId = 'Please select a sport.';
        }

        return errors;
      }}
      onSubmit={onFormSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, errors, handleSubmit, handleChange, setFieldValue }) => {
        const eligibleAlbumFolderTags = getEligibleTagsByIdentityGender(
          tags,
          tags.gender,
        );

        const formikErrors = Object.values(errors);

        return (
          <form onSubmit={handleSubmit} className={classes}>
            <div className="mb-2">
              <Label
                id="roster-identity-sport"
                labelType="expanded"
                label="Select Sport"
                for="sport-select"
              />{' '}
              <Select
                id="sport-select"
                name="sportId"
                value={values.sportId}
                disabled={!!selectedTag?.album_folder_tag.id}
                onChange={e => {
                  const newSportId = e.target.value;
                  setFieldValue('sportId', newSportId);
                  setFieldValue(
                    'configuringTagIds',
                    newSportId ? [newSportId] : [],
                  );
                }}
              >
                <option value="">Select</option>
                {eligibleAlbumFolderTags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="mb-2">
              {values &&
                values?.configuringTagIds.map(tagId => {
                  const tag = tags.find(({ id }) => id === tagId);

                  return (
                    <div className="mb-2" key={tagId}>
                      {values.selectedTags[tagId].map((selected, index) => {
                        const levels = tag?.levels?.records ?? [];
                        const selectedLevel = levels.find(
                          ({ id }) => id === selected.levelId,
                        );
                        const selectedLevelTeams =
                          selectedLevel?.teams?.records ?? [];
                        const fieldBase =
                          !!tag && `selectedTags.${tag.id}[${index}]`;

                        return (
                          <>
                            {' '}
                            {levels.length > 0 && (
                              <div className="mb-2">
                                <Label
                                  labelType="expanded"
                                  id="level-select"
                                  label="Level"
                                  for="level-select"
                                />

                                <Select
                                  id="level-select"
                                  name={`${fieldBase}.levelId`}
                                  onChange={event => {
                                    handleChange({
                                      target: {
                                        name: `${fieldBase}.levelId`,
                                        value: event.target.value,
                                      },
                                    });

                                    handleChange({
                                      target: {
                                        name: `${fieldBase}.teamId`,
                                        value: '',
                                      },
                                    });
                                  }}
                                  className="mb-2"
                                  value={selected.levelId}
                                >
                                  <option value="">Select your level...</option>
                                  {levels.map(level => (
                                    <option key={level.id} value={level.id}>
                                      {level.name}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                            )}
                            {selectedLevelTeams.length > 0 && (
                              <div className="mb-2">
                                <Select
                                  id="team-select"
                                  name={`${fieldBase}.teamId`}
                                  onChange={handleChange}
                                  className="mb-0"
                                  value={selected.teamId}
                                >
                                  <option value="">
                                    Team or Coach Name...
                                  </option>
                                  {selectedLevelTeams.map(team => (
                                    <option key={team.id} value={team.id}>
                                      {getTeamLabel(team)}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                            )}
                            {tag?.uniform_enabled && tag?.uniform_numbered && (
                              <div className="mb-2">
                                <Label
                                  labelType="expanded"
                                  id="jersey-select"
                                  label="Jersey number"
                                />
                                <Select
                                  id="jersey-number"
                                  name={`${fieldBase}.jerseyNumber`}
                                  value={selected.jerseyNumber}
                                  onChange={handleChange}
                                  placeholder="Enter your jersey number..."
                                  className="mb-0"
                                >
                                  <option value="">None</option>
                                  {jerseyNumbers.map(number => (
                                    <option key={number} value={number}>
                                      {number}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                            )}
                          </>
                        );
                      })}
                    </div>
                  );
                })}
            </div>

            <div>
              {(submitError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {submitError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              <Button
                btnBlock
                type="submit"
                disabled={!!submitting || values.configuringTagIds.length === 0}
              >
                {(() => {
                  if (submitting) {
                    return 'Loading...';
                  }
                  if (selectedTag) {
                    return 'Update';
                  }
                  return 'Save';
                })()}
              </Button>

              <Button
                btnStyle="link"
                className="text-center"
                btnBlock
                onClick={() => onCancel()}
              >
                Cancel
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

RosterIdentityTagAssignment.propTypes = propTypes;

export default RosterIdentityTagAssignment;
